/* ADR-012's local auth: sign up, log in, add a teammate.
 *
 * Partly generated. ADR-014's two shapes — `SignupAccepted` and `EmailVerificationResult` — are
 * emitted with their guards, because that registry was updated in the same change as the
 * contract. The rest are still hand-written against `contracts/openapi/tenancy.yaml`, which is
 * drift waiting to happen and is the argument for finishing the job rather than a reason not to
 * have started. `getPage`'s rule applies either way: a malformed response is refused rather than
 * rendered, so a real guard exists whether or not it was generated.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isAddUserResponse,
  isEmailVerificationResult,
  isSignupAccepted,
  type AddUserResponse,
  type EmailVerificationResult,
  type SignupAccepted,
} from '@/shared/api/generated/tenancy';
import { post, postJson } from '@/shared/api/http';
import { setToken } from '@/shared/auth/session';

const CONFIRM_VERIFICATION_URL = '/v1/auth/email-verifications/confirm';
const RESEND_VERIFICATION_URL = '/v1/auth/email-verifications/resend';
const SIGNUP_URL = '/v1/auth/signup';
const LOGIN_URL = '/v1/auth/login';
const CHANGE_PASSWORD_URL = '/v1/auth/password';
const ADD_USER_URL = '/v1/auth/users';
const VERIFY_REQUEST_URL = '/v1/auth/email-verifications';
const VERIFY_CHECK_URL = '/v1/auth/email-verifications/check';

export interface TokenResponse {
  readonly access_token: string;
  readonly token_type: string;
  /** Whether this session must set a new password before it can be used for anything else.
   *
   * **Optional on the type, and that is deliberate rather than lax.** `/login` and
   * `/v1/auth/password` both send it; `/signup` does not, because somebody who signed themselves up
   * chose their own password. A required field would make the guard below reject a perfectly good
   * signup response, so the *absence* of the field means no obligation — which is the same answer
   * the server gives. */
  readonly must_change_password?: boolean;
}

function isTokenResponse(value: unknown): value is TokenResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['access_token'] === 'string' &&
    typeof candidate['token_type'] === 'string' &&
    /* Present-and-boolean, or absent. A *string* `"true"` would be a server that changed shape, and
       reading it as truthy would send everybody to the change-password screen for ever. */
    (candidate['must_change_password'] === undefined ||
      typeof candidate['must_change_password'] === 'boolean')
  );
}

/* `AddUserResponse` and its guard are the **generated** ones now.
 *
 * The hand-written pair here declared `subject` and `role_name` and stopped, so the guard passed a
 * response carrying `welcome_email` and `must_change_password` while making both invisible to every
 * caller — and `welcome_email` is the one field that tells an administrator whether they still have
 * to pass the password on themselves. Both schemas were in `tenancy.yaml` and absent from `EMITTED`
 * in the generator, the seventh recorded instance of that registry failing open: it wrote
 * `tenancy.ts` without them and reported success, which is why this file grew its own copy. */

export interface SignupDraft {
  readonly email: string;
  readonly password: string;
  readonly display_name: string;
  /** The customer this tenancy belongs to. **Required, and unique across the deployment.**
   *
   * Not a nicety: without it a deployment cannot answer "which customer is this tenant", which is
   * the first question an operator reading an incident, an invoice or an audit entry asks. The
   * server enforces uniqueness on a *normalised* form — case folded and whitespace collapsed — so
   * "Bank A" and "  bank a " are one organisation rather than two tenants for one customer.
   *
   * Separate from `display_name`, which is the person. Signup previously passed one input as both,
   * so a field named for a human was doing double duty and the tenant's copy was discarded. */
  readonly organisation_name: string;
}

export interface LoginDraft {
  readonly email: string;
  readonly password: string;
}

export interface AddUserDraft {
  readonly email: string;
  readonly display_name: string;
  readonly role_name: string;
}
/* **No `password`, and its absence is the feature.** An administrator supplies a name, an address
 * and a role; the server creates the account with no password at all, mails a 48-hour single-use
 * activation link, and the invitee chooses the only password the account will ever have. So there
 * is no field through which an administrator could set one — the guarantee is a property of this
 * type rather than of a rule somebody remembers. */

/* One fixed key per mutable path, not `idempotencyKeyFor`'s per-subject caching. A signup or a login
 * is a single decision made once per form submission — the same shape `useVerifyConnection` and
 * `useRequestHarvest` use for a one-shot action — and `crypto.randomUUID()` per mount is enough: a
 * double-click reuses the same in-flight mutation because `useMutation` only tracks one call at a
 * time, and a genuinely new attempt (a different tab, a retry after closing the form) gets its own
 * key rather than replaying a decision that already failed for an unrelated reason. */
function freshKey(): string {
  return crypto.randomUUID();
}

/** Registers a tenant and its first admin. **Stores no token — ADR-014.**
 *
 * This hook used to call `setToken` on the response, because signup used to answer one: a successful
 * registration signed the caller straight in. It cannot any more, and the absence of that call is
 * the client half of the guarantee — the account is unverified, and authenticating one before its
 * address is proved is the thing the whole flow exists to prevent. The screen reads
 * `verification_required` and `delivered` instead and tells the person to check their mail.
 */
export function useSignUp(): UseMutationResult<SignupAccepted, Error, SignupDraft> {
  return useMutation({
    mutationFn: (draft: SignupDraft) =>
      post(SIGNUP_URL, freshKey(), isSignupAccepted, 'signup', draft),
    retry: false,
  });
}

/** Redeems a verification link, keyed on the token.
 *
 * **A query rather than a mutation, and that is a correctness fix rather than a preference.** The
 * previous shape fired a `useMutation` from a mount effect behind a `useRef` guard, and it could
 * not survive `StrictMode` — which `main.tsx` wraps the whole app in. React double-invokes every
 * effect in development: the first run sets the ref and fires the request, React then simulates an
 * unmount, and the second run finds the ref already true and fires nothing. In between, the
 * mutation observer was unsubscribed and resubscribed, so it came back **idle** and never received
 * the answer to the request that had already gone out. The result was a 200 on the wire and a
 * screen stuck on "Verifying your email" permanently — which is exactly what was reported.
 *
 * A query has no such gap: the answer lives in the `QueryClient` cache under
 * `['email-verification', token]`, so a resubscribing observer reads the cached result instead of
 * starting from nothing. It also needs no ref guard, which removes the mechanism that broke.
 *
 * **It is the honest shape anyway.** The route is in `NON_CREATING_POST_PATHS` precisely because a
 * replay is *expected* here — a double-click, a prefetching mail client and a restored tab are one
 * request arriving twice — so this is a read of "what does redeeming this token produce", which is
 * a value worth caching against the token. A mutation models an action somebody just asked for.
 *
 * `staleTime: Infinity` and `gcTime: Infinity`, so revisiting the same URL in one session shows the
 * first answer rather than spending a second request to be told `already_verified`. `retry: false`
 * because a 422 is the *expected* refusal — expired, spent, unknown — and retrying it three times
 * with backoff leaves the reader on the working card for seconds, which is the other half of the
 * stuck-looking report.
 *
 * **Stores no token**, for the reason a completed password reset does not: the person signs in
 * afterwards, which is the action that proves the address works. A mail link that authenticated
 * would make an inbox an authentication path, which is a wider thing than confirming an address.
 */
export function useConfirmEmailVerification(
  token: string | null,
): UseQueryResult<EmailVerificationResult> {
  return useQuery({
    queryKey: ['email-verification', token],
    // `enabled` below means this never runs with a null token; the assertion keeps that a type
    // error rather than a runtime `null` on the wire if the guard is ever loosened.
    queryFn: () =>
      postJson(CONFIRM_VERIFICATION_URL, { body: { token: token ?? '' } }).then(
        (body) => {
          if (!isEmailVerificationResult(body)) {
            throw new Error('the verification response did not match its schema');
          }
          return body;
        },
      ),
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Whether a resend request reached a mail transport. Mirrors the 202 body.
 *
 * **`delivered` is about the deployment, not about the address.** The server computes it from
 * whether a link transport is configured at all, so it is the same value for an address nobody has
 * ever used — which is what stops this route being an account-enumeration oracle. A screen must
 * therefore not read `true` as "that account exists"; it means "if there is an account here, a mail
 * has gone to it".
 */
export interface ResendAccepted {
  readonly delivered: boolean;
  readonly expires_in_minutes: number;
  /** Whether this deployment has a mail transport at all.
   *
   * **The field that separates the two readings of `delivered: false`.** No transport wired means
   * "ask an administrator"; a transport that refused means retrying is worth something. Also a
   * fact about the deployment rather than about the address, so it is the same value for an
   * address nobody has used — which is what keeps it out of the enumeration surface. */
  readonly transport_configured: boolean;
}

function isResendAccepted(value: unknown): value is ResendAccepted {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['delivered'] === 'boolean' &&
    typeof candidate['expires_in_minutes'] === 'number' &&
    typeof candidate['transport_configured'] === 'boolean'
  );
}

/** Asks for a fresh verification link. **Always succeeds, whatever the address.**
 *
 * A mutation rather than a query, and the contrast with `useConfirmEmailVerification` above is the
 * whole distinction: this is an action somebody just asked for by pressing a button, it sends mail,
 * and its answer is not a value worth caching against an input. Redeeming a link is the opposite —
 * it happens on mount, a replay is expected, and the result belongs to the token.
 *
 * Hand-written rather than generated: `EmailVerificationAccepted` is shared with the six-digit code
 * route and is not in `EMITTED`, which is the registry CLAUDE.md records failing *open* — it writes
 * the client and reports success. A guard exists either way, which is the part that matters.
 *
 * **An `Idempotency-Key` is sent and is required here**, unlike `/confirm`: this inserts a row and
 * mails a message, so a retried request must not issue a second link. `freshKey()` per call is
 * right for a button somebody presses — a double-click reuses the in-flight mutation, and a
 * genuinely new attempt minutes later is a new decision that should send a new mail.
 */
export function useResendEmailVerification(): UseMutationResult<
  ResendAccepted,
  Error,
  string
> {
  return useMutation({
    mutationFn: (email: string) =>
      post(RESEND_VERIFICATION_URL, freshKey(), isResendAccepted, 'verification link', {
        email,
      }),
    retry: false,
  });
}

/* ---------------------------------------------------------------- proving an address

 * A signup on a deployment with a mail transport is refused until the address has been proved, and
 * this pair is how the screen lets somebody prove one. Two calls rather than one because the two
 * halves are days apart in the worst case: a code is requested, and it is checked whenever the
 * person reads their mail.
 */

export interface VerificationRequested {
  /** **Whether a mail actually left the building, and it is the honest half.**
   *
   * `false` means this deployment has no mail transport — the request still answered 202, because
   * the response must say nothing about whether the address is registered, and that is deliberately
   * an account-enumeration defence rather than an accident. A screen that rendered "check your
   * email" from the status code alone would leave somebody waiting for a message nobody sent. */
  readonly delivered: boolean;
  readonly expires_in_minutes: number;
  /** **Which of the two undelivered cases this is, and they are opposite instructions.**
   *
   * `false` means no mail transport is wired — and the same absent mailer leaves signup's
   * verification gate unwired, so an address does not need proving and the person should just
   * create the account. `true` beside `delivered: false` means the transport refused: the gate
   * *is* armed, signup will be refused until a code arrives, and the person must retry or tell an
   * operator. A fact about the deployment, never about an address. */
  readonly transport_configured: boolean;
}

function isVerificationRequested(value: unknown): value is VerificationRequested {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['delivered'] === 'boolean' &&
    typeof candidate['expires_in_minutes'] === 'number' &&
    typeof candidate['transport_configured'] === 'boolean'
  );
}

export interface VerificationChecked {
  readonly email: string;
  readonly verified: boolean;
}

function isVerificationChecked(value: unknown): value is VerificationChecked {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate['email'] === 'string' && candidate['verified'] === true;
}

/** Asks for a code. Answers 202 whatever the address is — see `delivered`. */
export function useRequestEmailVerification(): UseMutationResult<
  VerificationRequested,
  Error,
  string
> {
  return useMutation({
    mutationFn: (email: string) =>
      post(
        VERIFY_REQUEST_URL,
        freshKey(),
        isVerificationRequested,
        'verification request',
        {
          email,
        },
      ),
    retry: false,
  });
}

export interface VerificationCheck {
  readonly email: string;
  readonly code: string;
}

/** Spends nothing and proves the address. Signup re-reads and consumes the stored proof, so this
 * returns no token — there is deliberately no second credential to leak or expire on its own. */
export function useCheckEmailVerification(): UseMutationResult<
  VerificationChecked,
  Error,
  VerificationCheck
> {
  return useMutation({
    mutationFn: (check: VerificationCheck) =>
      post(
        VERIFY_CHECK_URL,
        freshKey(),
        isVerificationChecked,
        'verification check',
        check,
      ),
    retry: false,
  });
}

const CHECK_INVITATION_URL = '/v1/auth/invitations/check';
const ACCEPT_INVITATION_URL = '/v1/auth/invitations/accept';

/** What the set-password screen needs, read off the stored invitation. */
export interface InvitationDetails {
  readonly email: string;
  readonly display_name: string;
  readonly expires_at: string;
}

function isInvitationDetails(value: unknown): value is InvitationDetails {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['email'] === 'string' &&
    typeof candidate['display_name'] === 'string' &&
    typeof candidate['expires_at'] === 'string'
  );
}

/** Validates an activation link and records that the address is proved.
 *
 * **A query keyed on the token, not a mutation**, for the reason `useConfirmEmailVerification`
 * records at length: a mutation fired from a mount effect cannot survive `StrictMode`, because the
 * observer is resubscribed between the double-invoked effects and comes back idle — so a request
 * that answered 200 is never read and the screen freezes. The answer lives in the client cache
 * here, so a resubscribing observer reads it.
 *
 * It is also the honest model. The route creates nothing, a replay is expected, and the result
 * belongs to the token — see `NON_CREATING_POST_PATHS`, where that reasoning is recorded.
 */
export function useCheckInvitation(
  token: string | null,
): UseQueryResult<InvitationDetails> {
  return useQuery({
    queryKey: ['invitation', token],
    queryFn: () =>
      postJson(CHECK_INVITATION_URL, { body: { token: token ?? '' } }).then((body) => {
        if (!isInvitationDetails(body)) {
          throw new Error('the invitation response did not match its schema');
        }
        return body;
      }),
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export interface ActivationDraft {
  readonly token: string;
  readonly password: string;
}

/** Sets the first password, activates the account, and signs the person in.
 *
 * **`setToken` on success, which `useConfirmEmailVerification` deliberately does not do.** The two
 * look alike and the difference is what other factor exists. There the account already had a
 * password its owner knew, so authenticating from a mail link would have added a *second* way in.
 * Here the account had no password until this request, so the inbox is the only possible factor:
 * somebody who read the mail can set the password whatever this returns, and withholding the
 * session would only make the person type what they just chose into a second form.
 *
 * `must_change_password` is `false` by construction — the password was chosen by its owner — so the
 * stored session carries no obligation.
 *
 * **`confirm_password` is not sent.** Matching two boxes is a typing check this client performs;
 * sending both would put a second copy of the password on the wire and in any request log that
 * captures bodies, and the server has nothing to compare it against that the client did not.
 *
 * No `Idempotency-Key`: the token is the deduplicating key and it is single-use at the substrate.
 */
export function useAcceptInvitation(): UseMutationResult<
  TokenResponse,
  Error,
  ActivationDraft
> {
  return useMutation({
    mutationFn: (draft: ActivationDraft) =>
      postJson(ACCEPT_INVITATION_URL, { body: { ...draft } }).then((body) => {
        if (!isTokenResponse(body)) {
          throw new Error('the activation response did not match its schema');
        }
        return body;
      }),
    onSuccess: (token) => {
      setToken(token.access_token, token.must_change_password === true);
    },
    retry: false,
  });
}

/** Sends a fresh invitation to a member who has not activated.
 *
 * Keyed on the subject rather than a body, because the route is
 * `POST /v1/auth/users/{subject}/invitation` — the person is the resource and the act is the same
 * `invite` permission that created them. A 409 means the account is already active, and the admin
 * panel offers this only for `pending_invitation` for exactly that reason.
 */
export function useResendInvitation(): UseMutationResult<ResendAccepted, Error, string> {
  return useMutation({
    mutationFn: (subject: string) =>
      post(
        `/v1/auth/users/${encodeURIComponent(subject)}/invitation`,
        freshKey(),
        isResendAccepted,
        'invitation',
        undefined,
      ),
    retry: false,
  });
}

export interface PasswordChangeDraft {
  readonly current_password: string;
  readonly new_password: string;
}

/** Replaces the caller's own password, and mints a token without the obligation.
 *
 * **The current password is required and that is not ceremony.** A bearer token is not sufficient
 * authority to replace the credential it authenticates — otherwise a stolen token becomes a
 * permanent account takeover. The server refuses without it.
 *
 * `setToken` with the fresh token is what actually unblocks the app: the obligation is a claim, so
 * the old token keeps being refused everywhere until it is replaced. Storing the new one with
 * `pending = false` is therefore not bookkeeping — it is the moment the session becomes usable.
 */
export function useChangePassword(): UseMutationResult<
  TokenResponse,
  Error,
  PasswordChangeDraft
> {
  return useMutation({
    mutationFn: (draft: PasswordChangeDraft) =>
      post(CHANGE_PASSWORD_URL, freshKey(), isTokenResponse, 'password change', draft),
    onSuccess: (token) => {
      setToken(token.access_token);
    },
    retry: false,
  });
}

export function useLogIn(): UseMutationResult<TokenResponse, Error, LoginDraft> {
  return useMutation({
    /* No `Idempotency-Key` header is sent by the backend's contract for this route, but `post`
     * requires one — harmless: `NON_CREATING_POST_PATHS` on the server exempts `/login` from the
     * requirement, so the header is accepted and ignored rather than rejected. */
    mutationFn: (draft: LoginDraft) =>
      post(LOGIN_URL, freshKey(), isTokenResponse, 'login', draft),
    onSuccess: (token) => {
      /* **The obligation is stored here, from the server's own answer.** Without this the app has a
         valid token and no idea that every route will refuse it, so the reader lands on a dashboard
         where each panel fails — which is exactly what happened before this line existed. */
      setToken(token.access_token, token.must_change_password === true);
    },
    retry: false,
  });
}

export function useAddUser(): UseMutationResult<AddUserResponse, Error, AddUserDraft> {
  return useMutation({
    mutationFn: (draft: AddUserDraft) =>
      post(ADD_USER_URL, freshKey(), isAddUserResponse, 'teammate', draft),
    retry: false,
  });
}
